import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, GripVertical, Eye, Upload, ArrowRight, Settings as SettingsIcon, Images, Copy, Video, Film } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (img) => {
  if (!img) return "";
  if (img.startsWith("http")) return img;
  if (img.startsWith("/api/")) return `${BACKEND_URL}${img}`;
  return img;
};

const BADGES = ["Nouveau", "Nouveau livre", "Événement", "Formation", "Livre", "Service", "Conférence"];
const STATUSES = ["published", "draft", "archived"];
const STATUS_LABELS = { published: "Publié", draft: "Brouillon", archived: "Archivé" };

const emptySlide = {
  title: "", subtitle: "", description: "", image: "", media_type: "image", video: "",
  video_autoplay: true, video_loop: true, video_muted: true, video_controls: false,
  full_image: false, button_text: "", button_link: "",
  button_color: "#C9A13A", badge: "", order: 0, status: "published", active: true,
  publish_date: "", end_date: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold mb-1.5">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full border border-gold/30 rounded-lg px-3.5 py-2.5 focus:border-gold focus:outline-none transition-colors";

const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brown-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-3xl" : "max-w-lg"} bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20 sticky top-0 bg-white z-10">
          <h3 className="font-heading text-2xl font-medium text-brown-black">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-brown-black/50 hover:text-brown-black"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const SlideMediaPreview = ({ slide, fit }) => {
  if (slide.media_type === "video" && slide.video) {
    return <video src={resolveImg(slide.video)} className={`w-full h-full ${fit}`} autoPlay={slide.video_autoplay !== false} loop={slide.video_loop !== false} muted controls={!!slide.video_controls} playsInline />;
  }
  return slide.image ? <img src={resolveImg(slide.image)} alt={slide.title} className={`w-full h-full ${fit}`} /> : null;
};

const SlidePreview = ({ slide }) => {
  if (slide.full_image) {
    return (
      <div className="rounded-2xl overflow-hidden border border-gold/40 shadow-lg h-[360px] bg-gradient-to-br from-[#FBF3DE] to-[#F3E4BC] flex items-center justify-center">
        <SlideMediaPreview slide={slide} fit="object-contain" />
      </div>
    );
  }
  return (
  <div className="rounded-2xl overflow-hidden border border-gold/40 shadow-lg grid grid-cols-1 md:grid-cols-2 h-[360px]">
    <div className="relative h-full overflow-hidden bg-[#0b1436]">
      <SlideMediaPreview slide={slide} fit="object-cover" />
      {slide.badge && <span className="absolute top-4 left-4 px-3.5 py-1.5 rounded-full bg-gold text-[#2C1A0E] text-xs font-bold uppercase tracking-wider">{slide.badge}</span>}
    </div>
    <div className="flex flex-col justify-center px-8 py-8 bg-gradient-to-br from-[#FBF3DE] to-[#F3E4BC]">
      {slide.subtitle && <p className="text-xs uppercase tracking-[0.28em] text-gold font-semibold mb-3">{slide.subtitle}</p>}
      <h3 className="font-heading text-3xl font-medium text-brown-black leading-tight">{slide.title || "Titre"}</h3>
      {slide.description && <p className="mt-3 text-sm text-brown-black/70 leading-relaxed">{slide.description}</p>}
      {slide.button_text && (
        <span className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm w-fit" style={{ backgroundColor: slide.button_color || "#C9A13A" }}>
          {slide.button_text} <ArrowRight size={16} />
        </span>
      )}
    </div>
  </div>
  );
};

const CarouselAdmin = () => {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptySlide);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [settingsModal, setSettingsModal] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/carousel/slides").then((r) => setSlides(r.data)).catch(() => {});
    api.get("/carousel/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...emptySlide, order: slides.length }); setEditId(null); setModal(true); };
  const openEdit = (s) => { setForm({ ...emptySlide, ...s }); setEditId(s.id); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: parseInt(form.order, 10) || 0 };
      if (editId) await api.put(`/admin/carousel/slides/${editId}`, payload);
      else await api.post("/admin/carousel/slides", payload);
      toast.success(editId ? "Diapositive mise à jour" : "Diapositive ajoutée");
      setModal(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette diapositive ?")) return;
    await api.delete(`/admin/carousel/slides/${id}`); toast.success("Diapositive supprimée"); load();
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/carousel/slides/${s.id}`, { ...s, active: !s.active });
    load();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/carousel/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: r.data.url }));
      toast.success("Image téléversée");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/carousel/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video: r.data.url, media_type: "video" }));
      toast.success("Vidéo téléversée");
    } catch { toast.error("Échec du téléversement de la vidéo"); }
    finally { setUploading(false); }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/carousel/slides/${id}/duplicate`);
      toast.success("Diapositive dupliquée (en brouillon)"); load();
    } catch { toast.error("Erreur lors de la duplication"); }
  };

  const onDrop = async (i) => {
    if (dragIndex === null || dragIndex === i) return;
    const arr = [...slides];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(i, 0, moved);
    setSlides(arr);
    setDragIndex(null);
    try {
      await api.put("/admin/carousel/reorder", { ids: arr.map((s) => s.id) });
      toast.success("Ordre mis à jour");
    } catch { toast.error("Erreur de réorganisation"); load(); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/carousel/settings", { ...settings, autoplay_speed: parseInt(settings.autoplay_speed, 10) || 5000 });
      toast.success("Réglages enregistrés"); setSettingsModal(false);
    } catch { toast.error("Erreur lors de l'enregistrement des réglages"); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h2 className="font-heading text-2xl text-brown-black">Gestion du carrousel</h2>
        <div className="flex gap-2">
          <button onClick={() => setSettingsModal(true)} data-testid="carousel-settings-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/40 text-gold text-sm font-semibold hover:bg-gold/10 transition-colors">
            <SettingsIcon size={16} /> Réglages
          </button>
          <button onClick={openNew} data-testid="carousel-add-slide" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brown-dark text-ivory text-sm font-semibold hover:bg-brown-black transition-colors">
            <Plus size={16} /> Ajouter une diapositive
          </button>
        </div>
      </div>
      <p className="text-sm text-brown-black/50 mb-5">Glissez-déposez les cartes pour réorganiser l'ordre d'affichage.</p>

      <div className="space-y-3">
        {slides.map((s, i) => (
          <div
            key={s.id}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            data-testid={`carousel-slide-row-${s.id}`}
            className="flex items-center gap-4 bg-white rounded-xl border border-gold/15 p-3 hover:border-gold/40 transition-colors"
          >
            <GripVertical size={18} className="text-brown-black/30 cursor-grab shrink-0" />
            {s.media_type === "video" && s.video ? (
              <div className="relative w-24 h-16 rounded-lg shrink-0 bg-[#0b1436] overflow-hidden flex items-center justify-center">
                <video src={resolveImg(s.video)} className="w-full h-full object-cover" muted />
                <Film size={18} className="absolute text-white/90 drop-shadow" />
              </div>
            ) : s.image ? (
              <img src={resolveImg(s.image)} alt={s.title} className="w-24 h-16 object-cover rounded-lg shrink-0 bg-[#0b1436]" />
            ) : (
              <div className="w-24 h-16 rounded-lg shrink-0 bg-[#0b1436]/10 flex items-center justify-center text-brown-black/30"><Images size={18} /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {s.media_type === "video" && <span className="text-[10px] uppercase tracking-wide bg-brown-dark text-ivory font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Video size={10} /> Vidéo</span>}
                {s.badge && <span className="text-[10px] uppercase tracking-wide bg-gold/15 text-gold-dark font-bold px-2 py-0.5 rounded-full">{s.badge}</span>}
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${s.status === "published" ? "bg-green-100 text-green-700" : s.status === "draft" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>{STATUS_LABELS[s.status]}</span>
              </div>
              <h3 className="font-heading text-lg text-brown-black truncate mt-0.5">{s.title}</h3>
              <p className="text-xs text-brown-black/50 truncate">{s.subtitle}</p>
            </div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-brown-dark cursor-pointer shrink-0" title="Actif / inactif">
              <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)} data-testid={`carousel-active-${s.id}`} className="w-4 h-4 accent-[#C9A13A]" />
              Actif
            </label>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setPreview(s)} title="Prévisualiser" className="w-9 h-9 rounded-lg border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/10"><Eye size={15} /></button>
              <button onClick={() => openEdit(s)} title="Modifier" className="w-9 h-9 rounded-lg border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/10"><Pencil size={15} /></button>
              <button onClick={() => duplicate(s.id)} data-testid={`carousel-duplicate-${s.id}`} title="Dupliquer" className="w-9 h-9 rounded-lg border border-gold/30 text-brown-dark/70 flex items-center justify-center hover:bg-gold/10"><Copy size={15} /></button>
              <button onClick={() => remove(s.id)} data-testid={`carousel-delete-${s.id}`} title="Supprimer" className="w-9 h-9 rounded-lg border border-red-200 text-red-500 flex items-center justify-center hover:bg-red-50"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {slides.length === 0 && <p className="text-center text-brown-black/40 py-10">Aucune diapositive. Cliquez sur « Ajouter une diapositive ».</p>}
      </div>

      {/* Add/Edit modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Modifier la diapositive" : "Nouvelle diapositive"} wide>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Field label="Titre"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="slide-title" /></Field></div>
          <div className="md:col-span-2"><Field label="Sous-titre"><input className={inputCls} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="slide-subtitle" /></Field></div>
          <div className="md:col-span-2"><Field label="Description"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="slide-description" /></Field></div>

          <div className="md:col-span-2">
            <Field label="Type de média">
              <div className="flex gap-3" data-testid="slide-media-type">
                <button type="button" onClick={() => setForm({ ...form, media_type: "image" })}
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border font-semibold text-sm transition-colors ${form.media_type !== "video" ? "bg-gold text-white border-gold" : "bg-white text-brown-dark border-gold/30"}`}>
                  <Images size={16} /> Image
                </button>
                <button type="button" onClick={() => setForm({ ...form, media_type: "video" })} data-testid="slide-media-video"
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border font-semibold text-sm transition-colors ${form.media_type === "video" ? "bg-gold text-white border-gold" : "bg-white text-brown-dark border-gold/30"}`}>
                  <Video size={16} /> Vidéo
                </button>
              </div>
            </Field>
          </div>

          {form.media_type === "video" ? (
            <div className="md:col-span-2 space-y-4">
              <Field label="Vidéo (MP4 / WebM)">
                <div className="flex items-center gap-3">
                  {form.video && <video src={resolveImg(form.video)} className="w-28 h-16 object-cover rounded-lg border border-gold/20 bg-[#0b1436]" muted />}
                  <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm font-semibold cursor-pointer hover:bg-gold/10 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload size={15} /> {uploading ? "Envoi…" : "Téléverser une vidéo"}
                    <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} data-testid="slide-video-upload" />
                  </label>
                </div>
                <input className={`${inputCls} mt-2`} placeholder="ou URL de la vidéo (.mp4)" value={form.video} onChange={(e) => setForm({ ...form, video: e.target.value })} data-testid="slide-video-url" />
              </Field>
              <Field label="Image de secours (poster, optionnel)">
                <div className="flex items-center gap-3">
                  {form.image && <img src={resolveImg(form.image)} alt="poster" className="w-24 h-16 object-cover rounded-lg border border-gold/20" />}
                  <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm font-semibold cursor-pointer hover:bg-gold/10">
                    <Upload size={15} /> Téléverser
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                  </label>
                </div>
              </Field>
              <div className="flex flex-wrap gap-x-6 gap-y-3 bg-ivory/60 rounded-lg p-4 border border-gold/15">
                {[
                  ["video_autoplay", "Lecture auto"],
                  ["video_loop", "Boucle"],
                  ["video_muted", "Sans son"],
                  ["video_controls", "Afficher les contrôles"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-brown-dark font-semibold cursor-pointer">
                    <input type="checkbox" checked={!!form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} data-testid={`slide-${key}`} className="w-4 h-4 accent-[#C9A13A]" /> {label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-brown-black/50">Pour la lecture automatique, la vidéo doit rester « sans son » (règle des navigateurs).</p>
            </div>
          ) : (
            <div className="md:col-span-2">
              <Field label="Image principale">
                <div className="flex items-center gap-3">
                  {form.image && <img src={resolveImg(form.image)} alt="aperçu" className="w-24 h-16 object-cover rounded-lg border border-gold/20" />}
                  <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm font-semibold cursor-pointer hover:bg-gold/10 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload size={15} /> {uploading ? "Envoi…" : "Téléverser"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="slide-upload" />
                  </label>
                </div>
                <input className={`${inputCls} mt-2`} placeholder="ou URL de l'image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="slide-image-url" />
              </Field>
            </div>
          )}

          <Field label="Texte du bouton"><input className={inputCls} value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} data-testid="slide-button-text" /></Field>
          <Field label="Lien du bouton (ex: /boutique)"><input className={inputCls} value={form.button_link} onChange={(e) => setForm({ ...form, button_link: e.target.value })} data-testid="slide-button-link" /></Field>
          <Field label="Couleur du bouton">
            <div className="flex items-center gap-2">
              <input type="color" value={form.button_color} onChange={(e) => setForm({ ...form, button_color: e.target.value })} className="w-12 h-10 rounded border border-gold/30" data-testid="slide-button-color" />
              <input className={inputCls} value={form.button_color} onChange={(e) => setForm({ ...form, button_color: e.target.value })} />
            </div>
          </Field>
          <Field label="Badge">
            <input className={inputCls} list="badge-options" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="slide-badge" />
            <datalist id="badge-options">{BADGES.map((b) => <option key={b} value={b} />)}</datalist>
          </Field>
          <Field label="Statut">
            <select className={`${inputCls} bg-white`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="slide-status">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </Field>
          <Field label="Ordre d'affichage"><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
          <Field label="Date de début (optionnelle)"><input type="datetime-local" className={inputCls} value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} /></Field>
          <Field label="Date de fin (optionnelle)"><input type="datetime-local" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>

          <div className="md:col-span-2 flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-brown-dark font-semibold cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#C9A13A]" /> Diapositive active
            </label>
            <label className="flex items-center gap-2 text-sm text-brown-dark font-semibold cursor-pointer">
              <input type="checkbox" checked={form.full_image} onChange={(e) => setForm({ ...form, full_image: e.target.checked })} data-testid="slide-full-image" className="w-4 h-4 accent-[#C9A13A]" /> Image pleine largeur (bannière)
            </label>
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="button" onClick={() => setPreview(form)} className="flex-1 py-3 rounded-full border border-gold/40 text-gold font-semibold hover:bg-gold/10 transition-colors inline-flex items-center justify-center gap-2"><Eye size={16} /> Prévisualiser</button>
            <button type="submit" data-testid="slide-submit" className="flex-1 py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors">Enregistrer</button>
          </div>
        </form>
      </Modal>

      {/* Preview modal */}
      <Modal open={!!preview} onClose={() => setPreview(null)} title="Prévisualisation" wide>
        {preview && <SlidePreview slide={preview} />}
      </Modal>

      {/* Settings modal */}
      <Modal open={settingsModal && !!settings} onClose={() => setSettingsModal(false)} title="Réglages du carrousel">
        {settings && (
          <form onSubmit={saveSettings} className="space-y-4">
            <Field label="Type de transition">
              <select className={`${inputCls} bg-white`} value={settings.transition} onChange={(e) => setSettings({ ...settings, transition: e.target.value })} data-testid="settings-transition">
                <option value="fade">Fondu (Fade)</option>
                <option value="slide">Glissement (Slide)</option>
                <option value="zoom">Zoom</option>
              </select>
            </Field>
            <Field label="Durée d'affichage par diapositive (ms)"><input type="number" className={inputCls} value={settings.autoplay_speed} onChange={(e) => setSettings({ ...settings, autoplay_speed: e.target.value })} data-testid="settings-speed" /></Field>
            {[
              ["autoplay", "Défilement automatique"],
              ["infinite", "Défilement infini"],
              ["arrows", "Afficher les flèches"],
              ["dots", "Afficher les indicateurs (points)"],
              ["pause_on_hover", "Pause au survol de la souris"],
              ["swipe", "Défilement tactile (mobile)"],
              ["show_on_mobile", "Afficher le carrousel sur mobile"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-brown-dark font-semibold cursor-pointer">
                <input type="checkbox" checked={!!settings[key]} onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })} data-testid={`settings-${key}`} className="w-4 h-4 accent-[#C9A13A]" /> {label}
              </label>
            ))}
            <button type="submit" data-testid="settings-submit" className="w-full py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors">Enregistrer les réglages</button>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default CarouselAdmin;
